"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Fade,
  Skeleton,
  Alert,
  Stack,
  useTheme,
  alpha,
  Pagination,
  Paper,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonRefresher,
  IonRefresherContent,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonFab,
  IonFabButton,
  IonIcon,
} from "@ionic/react";
import { refresh } from "ionicons/icons";
import { baseUrl, config, requestID } from "../../../common/utils";

const ITEMS_PER_PAGE = 8;

const RecommendationList: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [error, setError] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "cards">("cards");
  const [currentPage, setCurrentPage] = useState(1);

  const handleViewMore = (message: string) => {
    setSelectedMessage(message);
    setOpenModal(true);
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    setError("");
    setCurrentPage(1);

    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}/Onboardings/getAllRecommendations`,
        payload,
        config
      );
      console.log(res);
      const data = res.data.data || [];
      setRecommendations(data);
      setFilteredRecommendations(data);
    } catch (err) {
      console.error(err);
      setError(
        "Failed to fetch recommendations. Please check your API configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);

    if (!text.trim()) {
      setFilteredRecommendations(recommendations);
      return;
    }

    const filtered = recommendations.filter((rec: any) => {
      const name = rec.name || `${rec.firstName || ""} ${rec.lastName || ""}`;
      const email = rec.email || "";
      const message = rec.message || "";

      return (
        name.toLowerCase().includes(text.toLowerCase()) ||
        email.toLowerCase().includes(text.toLowerCase()) ||
        message.toLowerCase().includes(text.toLowerCase())
      );
    });

    setFilteredRecommendations(filtered);
  };

  const handleRefresh = async (event: CustomEvent) => {
    await fetchRecommendations();
    event.detail.complete();
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredRecommendations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRecommendations = filteredRecommendations.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const handleRecommendation = async (recommendation) => {
    try {
      const payload = {
        requestID: requestID(),
        recommendation_id: recommendation.recommendation_id,
        status: recommendation.status,
      }
   
      const res = await axios.post(
        `${baseUrl()}/Onboardings/approveRecommendations`,
        payload,
        config
      );
      console.log({res});
      const data = res.data.data || [];
     fetchRecommendations()
    } catch (err) {
      console.error(err);
      setError(
        "Failed to fetch recommendations. Please check your API configuration."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderCardView = () => (
    <Stack spacing={2} sx={{ mt: 2 }}>
      {loading
        ? Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
            <Card
              key={index}
              elevation={1}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.background.paper,
                  0.8
                )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
                backdropFilter: "blur(10px)",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Skeleton variant="circular" width={48} height={48} />
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="40%" height={20} />
                  </Box>
                </Box>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </CardContent>
            </Card>
          ))
        : paginatedRecommendations.map((rec: any, index: number) => {
            const name =
              rec.name || `${rec.firstName || ""} ${rec.lastName || ""}`;
            const displayMessage = rec.message || "No message provided";

            return (
              <Fade in key={index} timeout={300 + index * 100}>
                <Card
                  elevation={1}
                  sx={{
                    borderRadius: 3,
                    transition: "all 0.3s ease",
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.background.paper,
                      0.8
                    )} 0%, ${alpha(
                      theme.palette.background.default,
                      0.4
                    )} 100%)`,
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 12px 32px ${alpha(
                        theme.palette.primary.main,
                        0.15
                      )}`,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 48,
                          height: 48,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          boxShadow: `0 4px 12px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                        }}
                      >
                        {name ? getInitials(name) : <PersonIcon />}
                      </Avatar>

                      <Box sx={{ ml: 2, flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            mb: 0.5,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {name || "Anonymous"}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            icon={<EmailIcon />}
                            label={rec.email || "No email"}
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: "0.75rem",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Chip
                            icon={<CalendarIcon />}
                            label={formatDate(rec.created_at)}
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderRadius: 2,
                        p: 2,
                        border: `1px solid ${alpha(
                          theme.palette.primary.main,
                          0.08
                        )}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <MessageIcon
                          sx={{
                            fontSize: 16,
                            color: theme.palette.primary.main,
                            mr: 1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Recommendation Message
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          color: "text.primary",
                          fontFamily: "inherit",
                        }}
                      >
                        {displayMessage.length > 120 ? (
                          <>
                            {displayMessage.substring(0, 120)}...
                            <Button
                              size="small"
                              onClick={() => handleViewMore(displayMessage)}
                              sx={{
                                ml: 1,
                                minWidth: "auto",
                                textTransform: "none",
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                              }}
                              endIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            >
                              Read More
                            </Button>
                          </>
                        ) : (
                          displayMessage
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Button
                      disabled={rec.status == "1"}
                        color="warning"
                        onClick={()=>handleRecommendation(rec)}
                      >
                        {rec.status == "0" ? "Approve" : "Treated"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            );
          })}
    </Stack>
  );

  const renderTableView = () => (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        overflow: "hidden",
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.8
        )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
        backdropFilter: "blur(10px)",
      }}
    >
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                  py: 2,
                  borderBottom: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                }}
              >
                Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                  py: 2,
                  borderBottom: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                }}
              >
                Contact
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                  py: 2,
                  borderBottom: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                }}
              >
                Message
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                  py: 2,
                  borderBottom: `2px solid ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                  textAlign: "center",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton width="80%" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Skeleton
                          variant="circular"
                          width={32}
                          height={32}
                          sx={{ mr: 1 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="60%" />
                          <Skeleton width="40%" />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Skeleton width="90%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="60%" sx={{ mx: "auto" }} />
                    </TableCell>
                  </TableRow>
                ))
              : paginatedRecommendations.map((rec: any, index: number) => {
                  const name =
                    rec.name || `${rec.firstName || ""} ${rec.lastName || ""}`;
                  const displayMessage = rec.message || "No message provided";

                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={formatDate(rec.created_at)}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              mr: 1,
                              fontSize: "0.8rem",
                              bgcolor: theme.palette.primary.main,
                            }}
                          >
                            {name ? getInitials(name) : <PersonIcon />}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {name || "Anonymous"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {rec.email || "No email"}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {displayMessage.length > 50
                            ? `${displayMessage.substring(0, 50)}...`
                            : displayMessage}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        {displayMessage.length > 50 && (
                          <IconButton
                            size="small"
                            onClick={() => handleViewMore(displayMessage)}
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            {!loading && paginatedRecommendations.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchText
                      ? "No recommendations match your search."
                      : "No recommendations found."}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <IonPage>
      <IonHeader className="ion-padding">
        <IonToolbar>
          <IonTitle>Recommendations</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 400,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              Recommendation Requests
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: "1.1rem" }}
            >
              Manage and review all recommendation requests in one place
            </Typography>
          </Box>

          {/* Search and Controls */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.background.paper,
                0.8
              )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <IonSearchbar
              value={searchText}
              onIonInput={(e) => handleSearch(e.detail.value!)}
              placeholder="Search by name, email, or message..."
              showClearButton="focus"
            />

            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant={viewMode === "cards" ? "contained" : "outlined"}
                  onClick={() => setViewMode("cards")}
                  startIcon={<ViewModuleIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === "table" ? "contained" : "outlined"}
                  onClick={() => setViewMode("table")}
                  startIcon={<ViewListIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Table
                </Button>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {paginatedRecommendations.length} of{" "}
                  {filteredRecommendations.length} items
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchRecommendations}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Error State */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={fetchRecommendations}
                >
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* Content */}
          {viewMode === "cards" ? renderCardView() : renderTableView()}

          {/* Pagination */}
          {!loading && filteredRecommendations.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.background.paper,
                    0.8
                  )} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      fontWeight: 600,
                    },
                    "& .Mui-selected": {
                      boxShadow: `0 4px 12px ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}`,
                    },
                  }}
                />
              </Paper>
            </Box>
          )}

          {/* Floating Action Button */}
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton
              onClick={fetchRecommendations}
              disabled={loading}
              style={{
                "--background": theme.palette.primary.main,
                "--background-activated": theme.palette.primary.dark,
              }}
            >
              <IonIcon icon={refresh} />
            </IonFabButton>
          </IonFab>

          {/* Message Modal */}
          <Dialog
            open={openModal}
            onClose={() => setOpenModal(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: `0 24px 48px ${alpha(
                  theme.palette.common.black,
                  0.2
                )}`,
                background: `linear-gradient(135deg, ${
                  theme.palette.background.paper
                } 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
                backdropFilter: "blur(20px)",
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                pb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MessageIcon
                  sx={{ mr: 1, color: theme.palette.primary.main }}
                />
                Full Recommendation Message
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <DialogContentText
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.7,
                  fontSize: "1.1rem",
                  color: "text.primary",
                  fontFamily: "inherit",
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                {selectedMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 2 }}>
              <Button
                onClick={() => setOpenModal(false)}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default RecommendationList;
